
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import enTranslations from './locales/en.json';
import jaTranslations from './locales/ja.json';

// Create a robust backup system for translations
const BACKUP_VERSION = '1.2'; // Increment this when making significant changes to translations

// Helper to flatten nested objects into dot notation keys
const flattenKeys = (obj: any, prefix = '') => {
  return Object.keys(obj).reduce((acc: string[], key) => {
    const newPrefix = prefix ? `${prefix}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      acc.push(...flattenKeys(obj[key], newPrefix));
    } else {
      acc.push(newPrefix);
    }
    return acc;
  }, []);
};

// Store original translations as deep copies to prevent accidental mutation
const originalTranslations = {
  en: JSON.parse(JSON.stringify(enTranslations)),
  ja: JSON.parse(JSON.stringify(jaTranslations))
};

// Create an immutable reference of the original keys structure
// This will be used to ensure that no keys get deleted
const immutableKeyStructure = {
  en: flattenKeys(originalTranslations.en),
  ja: flattenKeys(originalTranslations.ja)
};

// Implementation of ensureNoKeysDeletion function
// This function ensures that no keys are deleted from the translations
const ensureNoKeysDeletion = () => {
  // Check if any keys have been removed from the immutable key structure
  const currentEnKeys = flattenKeys(enTranslations);
  const currentJaKeys = flattenKeys(jaTranslations);
  
  // Find keys that exist in the immutable structure but not in the current translations
  const deletedFromEn = immutableKeyStructure.en.filter(key => !currentEnKeys.includes(key));
  const deletedFromJa = immutableKeyStructure.ja.filter(key => !currentJaKeys.includes(key));
  
  // Log the deleted keys if any
  if (deletedFromEn.length > 0) {
    console.error('🚨 Keys deleted from English translations:', deletedFromEn);
    
    // Restore the deleted keys from the original translations
    for (const key of deletedFromEn) {
      const keyPath = key.split('.');
      const value = getNestedValue(originalTranslations.en, keyPath);
      if (value !== undefined) {
        setNestedValue(enTranslations, keyPath, value);
        console.log(`✅ Restored English key: ${key}`);
      }
    }
  }
  
  if (deletedFromJa.length > 0) {
    console.error('🚨 Keys deleted from Japanese translations:', deletedFromJa);
    
    // Restore the deleted keys from the original translations
    for (const key of deletedFromJa) {
      const keyPath = key.split('.');
      const value = getNestedValue(originalTranslations.ja, keyPath);
      if (value !== undefined) {
        setNestedValue(jaTranslations, keyPath, value);
        console.log(`✅ Restored Japanese key: ${key}`);
      }
    }
  }
  
  // Return status
  return {
    deletedFromEn,
    deletedFromJa,
    restoredEn: deletedFromEn.length > 0,
    restoredJa: deletedFromJa.length > 0
  };
};

// Backup translations to localStorage with versioning
const backupTranslations = () => {
  const timestamp = new Date().toISOString();
  const backup = {
    version: BACKUP_VERSION,
    timestamp,
    translations: {
      en: originalTranslations.en,
      ja: originalTranslations.ja
    },
    keyStructure: immutableKeyStructure
  };
  
  localStorage.setItem('i18n_backup', JSON.stringify(backup));
  console.log(`Translations backed up at ${timestamp} (v${BACKUP_VERSION})`);
};

// Restore translations from backup if needed (for development purposes)
const restoreFromBackup = () => {
  try {
    const backup = localStorage.getItem('i18n_backup');
    if (backup) {
      const parsed = JSON.parse(backup);
      console.log(`Found translation backup from ${parsed.timestamp} (v${parsed.version})`);
      return parsed.translations;
    }
  } catch (error) {
    console.error('Failed to restore translations from backup:', error);
  }
  return null;
};

// Helper to get nested value using path array
const getNestedValue = (obj: any, path: string[]) => {
  let current = obj;
  for (let i = 0; i < path.length; i++) {
    if (current === undefined || current === null) return undefined;
    current = current[path[i]];
  }
  return current;
};

// Helper to set nested value using path array
const setNestedValue = (obj: any, path: string[], value: any) => {
  let current = obj;
  
  // Traverse through object until we reach the parent of the last key
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    // Create the path if it doesn't exist
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    current = current[key];
  }
  
  // Set the value for the last key
  const lastKey = path[path.length - 1];
  current[lastKey] = value;
  
  return obj;
};

// Check for missing translations by comparing language keys
const findMissingTranslations = () => {
  if (process.env.NODE_ENV !== 'development') return;
  
  const enKeys = flattenKeys(enTranslations);
  const jaKeys = flattenKeys(jaTranslations);
  
  const missingInJa = enKeys.filter(key => !jaKeys.includes(key));
  const missingInEn = jaKeys.filter(key => !enKeys.includes(key));
  
  if (missingInJa.length > 0) {
    console.warn('🔴 Keys missing in Japanese translations:', missingInJa);
  }
  
  if (missingInEn.length > 0) {
    console.warn('🔴 Keys missing in English translations:', missingInEn);
  }
  
  console.log('Translation validation complete. EN:', enKeys.length, 'keys, JA:', jaKeys.length, 'keys');
};

// Enhanced backup function that merges with existing backup
const enhancedBackupTranslations = () => {
  try {
    const existingBackup = localStorage.getItem('i18n_backup');
    if (existingBackup) {
      const parsed = JSON.parse(existingBackup);
      const mergedBackup = {
        version: BACKUP_VERSION,
        timestamp: new Date().toISOString(),
        translations: {
          en: { ...parsed.translations.en, ...enTranslations },
          ja: { ...parsed.translations.ja, ...jaTranslations }
        },
        keyStructure: immutableKeyStructure
      };
      localStorage.setItem('i18n_backup', JSON.stringify(mergedBackup));
      console.log(`Enhanced translations backup created at ${mergedBackup.timestamp}`);
    } else {
      backupTranslations();
    }
  } catch (error) {
    console.error('Error in enhanced backup:', error);
    backupTranslations(); // Fallback to regular backup
  }
};

// NEW UTILITY FUNCTION: Helps update a specific translation key without affecting others
export const updateTranslationKey = (key: string, valueEN: string, valueJA: string) => {
  // Split the key into path components
  const keyPath = key.split('.');
  
  // Create deep copies to avoid mutating original objects
  const updatedEN = JSON.parse(JSON.stringify(enTranslations));
  const updatedJA = JSON.parse(JSON.stringify(jaTranslations));
  
  // Apply the specific updates
  setNestedValue(updatedEN, keyPath, valueEN);
  setNestedValue(updatedJA, keyPath, valueJA);
  
  // Log the specific change
  console.log(`Translation key updated: ${key}`);
  console.log(`- EN: "${valueEN}"`);
  console.log(`- JA: "${valueJA}"`);
  
  // Return the updated translations
  return {
    en: updatedEN,
    ja: updatedJA
  };
};

// Critical keys that must always be present - payment methods and groups keys
const CRITICAL_KEYS = {
  groups: ['createdAt', 'enterGroupName'],
  payments: [
    'savings', 
    'checking', 
    'accountNumber', 
    'accountNumberPlaceholder', 
    'accountHolder', 
    'accountHolderPlaceholder',
    'paypayDetails',
    'bankDetails',
    'bankDetailsDescription'
  ]
};

// Enhance the validation to specifically check for critical keys
const validateCriticalKeys = () => {
  for (const namespace in CRITICAL_KEYS) {
    const keys = CRITICAL_KEYS[namespace];
    for (const key of keys) {
      const fullKey = `${namespace}.${key}`;
      if (!jaTranslations[namespace]?.[key] || !enTranslations[namespace]?.[key]) {
        console.error(`🚨 Critical key missing: ${fullKey}`);
        const backup = restoreFromBackup();
        if (backup) {
          if (!jaTranslations[namespace]) jaTranslations[namespace] = {};
          if (!enTranslations[namespace]) enTranslations[namespace] = {};
          
          // Restore only the specific missing key from backup if available
          if (backup.translations.ja[namespace]?.[key]) {
            jaTranslations[namespace][key] = backup.translations.ja[namespace][key];
          } else {
            // If no backup for Japanese, try to use English as fallback for UI consistency
            jaTranslations[namespace][key] = enTranslations[namespace]?.[key] || key;
            console.warn(`⚠️ No backup for key "${fullKey}" in JA, using EN as fallback`);
          }
          
          if (backup.translations.en[namespace]?.[key]) {
            enTranslations[namespace][key] = backup.translations.en[namespace][key];
          }
          
          console.log(`✅ Restored critical key: ${fullKey}`);
        } else {
          // If no backup at all, create the keys to prevent UI breakage
          if (!jaTranslations[namespace]) jaTranslations[namespace] = {};
          if (!enTranslations[namespace]) enTranslations[namespace] = {};
          
          // Use English as fallback if available, otherwise use the key itself
          jaTranslations[namespace][key] = enTranslations[namespace]?.[key] || key;
          enTranslations[namespace][key] = enTranslations[namespace]?.[key] || key;
          
          console.warn(`⚠️ No backup available, created placeholder for "${fullKey}"`);
        }
      }
    }
  }
};

// NEW: MutationObserver to detect changes to the translations objects
const setupTranslationsProtection = () => {
  let lastEnKeys = flattenKeys(enTranslations);
  let lastJaKeys = flattenKeys(jaTranslations);
  
  // Periodically check if keys have been deleted
  const intervalId = setInterval(() => {
    // Get current keys
    const currentEnKeys = flattenKeys(enTranslations);
    const currentJaKeys = flattenKeys(jaTranslations);
    
    // Check if any keys have been deleted
    const deletedFromEn = lastEnKeys.filter(key => !currentEnKeys.includes(key));
    const deletedFromJa = lastJaKeys.filter(key => !currentJaKeys.includes(key));
    
    if (deletedFromEn.length > 0 || deletedFromJa.length > 0) {
      console.error('🚨 Translation keys deleted detected in real-time check!');
      ensureNoKeysDeletion();
    }
    
    // Update last known keys
    lastEnKeys = currentEnKeys;
    lastJaKeys = currentJaKeys;
  }, 5000); // Check every 5 seconds
  
  // For cleanup (if needed)
  return () => clearInterval(intervalId);
};

// Run initial validation and setup protection
enhancedBackupTranslations();
findMissingTranslations();
validateCriticalKeys();
ensureNoKeysDeletion();

// Setup real-time protection
setupTranslationsProtection();

// Run validation again after a short delay to catch any asynchronous issues
setTimeout(() => {
  console.log("Running delayed validation check for critical translations...");
  validateCriticalKeys();
  ensureNoKeysDeletion();
  enhancedBackupTranslations();
}, 2000);

// Initialize i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: {
        translation: enTranslations,
      },
      ja: {
        translation: jaTranslations,
      },
    },
    fallbackLng: 'ja',
    lng: localStorage.getItem('i18nextLng') || 'ja', // Use stored language or default to Japanese
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },
    interpolation: {
      escapeValue: false,
    },
    // Add debug mode in development environment
    debug: process.env.NODE_ENV === 'development',
    saveMissing: true,
    missingKeyHandler: (lng, ns, key) => {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`🔍 TRANSLATION MISSING: [${lng}] ${key}`);
      }
    },
    // Ensure translations are always loaded
    react: {
      useSuspense: false,
    }
  });

// NEW: Hook into i18next's events to ensure keys aren't deleted during runtime
i18n.on('initialized', () => {
  console.log('i18next initialized, setting up key protection...');
  // After initialization, perform the check again
  ensureNoKeysDeletion();
});

// Export a function to validate translations (can be called from dev tools)
export const validateTranslations = () => {
  findMissingTranslations();
  ensureNoKeysDeletion();
  return {
    backupExists: !!localStorage.getItem('i18n_backup'),
    keyCounts: {
      en: flattenKeys(enTranslations).length,
      ja: flattenKeys(jaTranslations).length
    },
    enTranslations,
    jaTranslations,
    immutableKeyStructure
  };
};

// Export the current translations for debugging
export const getCurrentTranslations = () => ({
  en: enTranslations,
  ja: jaTranslations
});

// Create a utility to manually backup translations
export const manualBackupTranslations = () => {
  enhancedBackupTranslations();
  return { success: true, timestamp: new Date().toISOString(), version: BACKUP_VERSION };
};

// Create a utility to restore translations from backup and reload the page
export const restoreTranslationsFromBackup = () => {
  const backup = restoreFromBackup();
  if (backup) {
    console.log('Restored translations from backup. Reloading page to apply...');
    window.location.reload();
    return { success: true };
  }
  return { success: false, error: 'No backup found' };
};

// NEW: Function to verify all keys are intact
export const verifyTranslationIntegrity = () => {
  ensureNoKeysDeletion();
  return { 
    intact: true, 
    verified: new Date().toISOString()
  };
};

export default i18n;
